import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Settings, BarChart3, Eye, Trash2, Edit3 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { fetchMentorCommunities, deleteCommunity, fetchCommunityStats } from "@/services/mentorCommunityService";
import { CommunityWithDetails } from "@/types/community";

interface CommunityWithStats extends CommunityWithDetails {
  post_count?: number;
  like_count?: number;
  comment_count?: number;
}

const ComunidadesMentor = () => {
  const [communities, setCommunities] = useState<CommunityWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const { user } = useAuth();
  const { role, loading: roleLoading } = useRole();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Função para carregar comunidades
  const loadCommunities = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      console.log('📋 [ComunidadesMentor] Carregando comunidades do mentor...');
      
      const data = await fetchMentorCommunities(user.id);
      console.log('✅ [ComunidadesMentor] Comunidades carregadas:', data.length);
      
      // Inicializar com dados básicos
      const communitiesWithStats: CommunityWithStats[] = data.map(community => ({
        ...community,
        post_count: 0,
        like_count: 0,
        comment_count: 0
      }));
      
      setCommunities(communitiesWithStats);
      
      // Carregar estatísticas em background
      loadCommunitiesStats(data);
      
    } catch (error) {
      console.error('❌ [ComunidadesMentor] Erro ao carregar comunidades:', error);
      toast({
        title: "Erro",
        description: "Falha ao carregar suas comunidades",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para carregar estatísticas das comunidades
  const loadCommunitiesStats = async (communitiesData: CommunityWithDetails[]) => {
    if (!user?.id) return;
    
    try {
      setStatsLoading(true);
      console.log('📊 [ComunidadesMentor] Carregando estatísticas das comunidades...');
      
      const communitiesWithStats = await Promise.all(
        communitiesData.map(async (community) => {
          try {
            const stats = await fetchCommunityStats(community.id, user.id);
            console.log(`✅ [ComunidadesMentor] Stats para ${community.name}:`, stats);
            
            return {
              ...community,
              member_count: stats.memberCount || community.member_count || 0,
              post_count: stats.postCount || 0,
              like_count: stats.likeCount || 0,
              comment_count: stats.commentCount || 0
            };
          } catch (error) {
            console.warn(`⚠️ [ComunidadesMentor] Erro ao carregar stats para ${community.name}:`, error);
            return {
              ...community,
              post_count: 0,
              like_count: 0,
              comment_count: 0
            };
          }
        })
      );
      
      setCommunities(communitiesWithStats);
      console.log('✅ [ComunidadesMentor] Estatísticas carregadas para todas as comunidades');
      
    } catch (error) {
      console.error('❌ [ComunidadesMentor] Erro ao carregar estatísticas:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Verificar se é mentor
  useEffect(() => {
    if (!roleLoading && role && role !== 'mentor') {
      navigate('/');
    }
  }, [role, roleLoading, navigate]);

  useEffect(() => {
    if (user?.id && role === 'mentor') {
      loadCommunities();
    }
  }, [user, role]);

  // Retornar loading se ainda está verificando a role
  if (roleLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Retornar null se não é mentor (vai ser redirecionado)
  if (role !== 'mentor') {
    return null;
  }

  const handleDeleteCommunity = async (communityId: string) => {
    if (!user?.id) return;
    
    if (!confirm('Tem certeza que deseja deletar esta comunidade? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await deleteCommunity(communityId, user.id);
      toast({
        title: "Sucesso",
        description: "Comunidade deletada com sucesso",
      });
      loadCommunities(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao deletar comunidade:', error);
      toast({
        title: "Erro",
        description: "Falha ao deletar comunidade",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Minhas Comunidades
            </h1>
            <p className="text-gray-400">
              Gerencie suas comunidades e acompanhe o engajamento
              {statsLoading && <span className="text-blue-400 ml-2">• Carregando estatísticas...</span>}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              onClick={async () => {
                const currentCommunities = communities.map(({ post_count, like_count, comment_count, ...community }) => community);
                await loadCommunitiesStats(currentCommunities);
              }}
              variant="outline"
              disabled={statsLoading}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {statsLoading ? 'Atualizando...' : 'Atualizar Stats'}
            </Button>
            <Button 
              onClick={() => navigate('/painel-mentor/comunidades/criar')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Comunidade
            </Button>
          </div>
        </div>

        {/* Estatísticas Gerais */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total de Comunidades</p>
                  <p className="text-2xl font-bold text-white">
                    {communities.length}
                  </p>
                </div>
                <div className="bg-blue-600 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total de Membros</p>
                  <p className="text-2xl font-bold text-white">
                    {communities.reduce((total, community) => total + (community.member_count || 0), 0)}
                  </p>
                </div>
                <div className="bg-green-600 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total de Posts</p>
                  <p className="text-2xl font-bold text-white">
                    {communities.reduce((total, community) => total + (community.post_count || 0), 0)}
                  </p>
                </div>
                <div className="bg-purple-600 p-3 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total de Likes</p>
                  <p className="text-2xl font-bold text-white">
                    {communities.reduce((total, community) => total + (community.like_count || 0), 0)}
                  </p>
                </div>
                <div className="bg-red-600 p-3 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lista de Comunidades */}
        {communities.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                Nenhuma comunidade ainda
              </h3>
              <p className="text-gray-400 mb-6">
                Crie sua primeira comunidade para começar a conectar com seus alunos
              </p>
              <Button 
                onClick={() => navigate('/painel-mentor/comunidades/criar')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeira Comunidade
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {communities.map((community: any) => (
              <Card key={community.id} className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-colors">
                <CardHeader className="pb-4">
                  {/* Imagem de Capa ou Gradiente */}
                  <div className="relative h-32 rounded-lg mb-4 overflow-hidden">
                    {(community.cover_image_url || community.cover_image) ? (
                      <img 
                        src={community.cover_image_url || community.cover_image} 
                        alt={community.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.log("❌ Erro ao carregar imagem da comunidade:", community.cover_image_url || community.cover_image);
                          // Fallback para gradiente se a imagem falhar
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="w-full h-full bg-gradient-to-r from-blue-600 to-purple-600"></div>';
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-blue-600 to-purple-600" />
                    )}
                    
                    {/* Status Badge */}
                    <div className="absolute top-2 right-2">
                      <Badge 
                        variant={community.is_active ? "default" : "secondary"}
                        className={community.is_active ? "bg-green-600" : "bg-gray-600"}
                      >
                        {community.is_active ? "Ativa" : "Inativa"}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mb-2">
                    {community.games?.icon && (
                      <img 
                        src={community.games.icon} 
                        alt={community.games.name}
                        className="w-8 h-8 rounded"
                      />
                    )}
                    <div>
                      <CardTitle className="text-lg text-white">
                        {community.name}
                      </CardTitle>
                      <p className="text-sm text-gray-400">
                        {community.games?.name}
                      </p>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Descrição */}
                  {community.description && (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {community.description}
                    </p>
                  )}

                  {/* Estatísticas */}
                  <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                    <div>
                      <p className="text-lg font-semibold text-white">
                        {community.member_count || 0}
                      </p>
                      <p className="text-xs text-gray-400">Membros</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-white">
                        {community.post_count || 0}
                      </p>
                      <p className="text-xs text-gray-400">Posts</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-white">
                        {community.like_count || 0}
                      </p>
                      <p className="text-xs text-gray-400">Likes</p>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/comunidades/${community.slug}`)}
                      className="flex-1 border-gray-600 hover:bg-gray-700"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/painel-mentor/comunidades/${community.id}/editar`)}
                      className="flex-1 border-gray-600 hover:bg-gray-700"
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/painel-mentor/comunidades/${community.id}/dashboard`)}
                      className="border-gray-600 hover:bg-gray-700"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteCommunity(community.id)}
                      className="border-red-600 text-red-400 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComunidadesMentor; 